import { Careprovider, QueryProgress } from "core/types";
import InfoCaption from "ds_legacy/components/InfoCaption";
import LinkV2 from "ds_legacy/components/LinkV2";
import RSButtonSaveProgress from "ds_legacy/components/RSButton/RSButtonSaveProgress";
import { ICON_GREY } from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, sizing } from "ds_legacy/materials/metrics";
import {
  FONT_SIZE_20,
  FONT_WEIGHT_BOLD,
  Title,
} from "ds_legacy/materials/typography";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { FormValidationNotification } from "dsl/organisms/Notifications";
import React, { ReactNode } from "react";
import { To } from "react-router-dom";
import styled from "styled-components";
import { TranslationComposition } from "translations/helpers";

export const FORM_WIDTH = dp(400);

export const FieldContainer = styled.div<{
  alignItems?: string;
  justify?: string;
  margin?: string;
  width?: string;
}>`
  display: flex;
  flex: 1 1 auto;
  flex-direction: row;
  justify-content: ${(props) => props.justify || undefined};
  align-items: ${(props) => props.alignItems || "flex-end"};
  width: ${(props) => props.width || undefined};
  margin: ${(props) => props.margin || margin(0)};
`;

export const CareproviderInfosBox = styled.div<{ hide?: boolean }>`
  margin: ${margin(0, 0, 2, 0)};
  display: ${({ hide }) => (hide ? "none" : "flex")};
  align-items: stretch;
  flex-direction: column;
  flex: 1 0 auto;
`;

export const FormBox = styled.div<{ margin?: string }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin: ${(props) => props.margin};
  @media screen and (max-width: ${FORM_WIDTH}) {
    & {
      max-width: 100vw;
    }
  }

  @media screen and (min-width: ${dp(420)}) {
    & {
      width: ${FORM_WIDTH};
    }
  }

  flex: 1 0 auto;
`;

export const iconStyle = {
  color: ICON_GREY,
  fontSize: FONT_SIZE_20,
  margin: margin(0, 0, 2, 0),
};

export function getTranslationCompositionHint(translation: string, to?: To) {
  return (
    <TranslationComposition translations={translation} withOptions>
      {([before, link, after]) => (
        <span>
          {before}
          {to ? <LinkV2 to={to}>{link}</LinkV2> : link}
          {after}
        </span>
      )}
    </TranslationComposition>
  );
}

export function HintWrapper({
  caption,
  children = undefined,
  desktopOnly = false,
  disabled = false,
  disconnected = false,
  hintMargin,
}: {
  caption: React.JSX.Element | string;
  children?: React.ReactNode;
  desktopOnly?: boolean;
  disabled?: boolean;
  disconnected?: boolean;
  hintMargin?: string;
}) {
  const { isDesktop, isMobile, isTablet } = useMedia();
  const hintWidth = () => {
    if (isMobile) return sizing(32);
    if (isTablet) return sizing(40);
    if (isDesktop) return sizing(36);
  };
  if (disabled || (desktopOnly && !isDesktop)) {
    return <>{children}</>;
  }
  return (
    <div style={{ position: "relative" }}>
      {children}
      <HorizontalLayout
        style={
          isDesktop
            ? {
                left: sizing(60),
                margin: hintMargin,
                position: "absolute",
                top: sizing(disconnected ? -5 : 1.5),
              }
            : { margin: margin(0, 0, 0, 4.5) }
        }
        width={hintWidth()}
      >
        <InfoCaption>{caption}</InfoCaption>
      </HorizontalLayout>
    </div>
  );
}

export function showExtensionsTab({
  careprovider,
}: {
  careprovider?: Careprovider | null;
}) {
  return (
    !!careprovider?.extensions?.length &&
    careprovider?.address?.country === "DE"
  );
}

export function SaveButton({
  children,
  id,
  onSubmitQueryProgress,
  submit,
}: {
  children?: React.ReactNode;
  id?: string;
  onSubmitQueryProgress: QueryProgress;
  submit: () => void;
}) {
  return (
    <div
      className="mx-2 my-2 flex flex-col items-start px-3"
      data-testid="save-button"
      id={id}
    >
      {children}
      <RSButtonSaveProgress
        queryProgress={onSubmitQueryProgress}
        style={{ minWidth: 150 }}
        onSubmit={submit}
      />
      <FormValidationNotification />
    </div>
  );
}

export function SettingsPageTitle({
  children,
  title,
}: {
  children?: ReactNode;
  title: string;
}) {
  const { isTablet } = useMedia();

  const Layout = isTablet ? VerticalLayout : HorizontalLayout;

  return (
    <Layout
      style={{
        alignItems: "baseline",
        gap: isTablet ? sizing(0.5) : sizing(2),
        margin: margin(2, 0),
      }}
    >
      <Title margin={margin(0)} style={{ fontWeight: FONT_WEIGHT_BOLD }}>
        {title}
      </Title>
      {children}
    </Layout>
  );
}
